import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetUserPreferences } from "../api/queries";
import { useDismissTutorial } from "../api/mutations";

// Global state to track all visible tutorials and their priorities
const visibleTutorials: Set<string> = new Set();
let tutorialPriorities: { [tutorialId: string]: number } = {};
let tutorialInstances: { [tutorialId: string]: string[] } = {}; // Track all component instances for each tutorialId
const listeners: Set<() => void> = new Set();

// Tutorial ordering system
let tutorialCounter = 0;

// Function to notify all components when visible tutorials change
const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

// Function to get the highest priority visible tutorial
const getHighestPriorityTutorial = (): string | null => {
  let highestPriority = -1;
  let highestPriorityTutorial: string | null = null;

  for (const tutorialId of visibleTutorials) {
    const priority = tutorialPriorities[tutorialId] || 0;
    if (priority > highestPriority) {
      highestPriority = priority;
      highestPriorityTutorial = tutorialId;
    }
  }

  return highestPriorityTutorial;
};

// Function to get the first instance of a tutorial that should be visible
const getFirstInstanceOfTutorial = (tutorialId: string): string | null => {
  const instances = tutorialInstances[tutorialId] || [];
  return instances.length > 0 ? instances[0] : null;
};

// Reset tutorial counter (call this on page navigation)
export const resetTutorialCounter = () => {
  tutorialCounter = 0;
  tutorialPriorities = {};
  tutorialInstances = {};
  visibleTutorials.clear();
  notifyListeners();
};

export type TutorialTipDirection =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "topleft"
  | "topright"
  | "bottomleft"
  | "bottomright"
  | "lefttop"
  | "leftbottom"
  | "righttop"
  | "rightbottom";

export interface TutorialTipProps {
  children: React.ReactNode;
  direction: TutorialTipDirection;
  content: React.ReactNode;
  tutorialId: string;
  onClose?: () => void;
  maxWidth?: number;
  className?: string;
  isVisible?: boolean;
}

export const TutorialTip: React.FC<TutorialTipProps> = ({
  children,
  direction,
  onClose,
  content = "This is a tutorial tip!",
  maxWidth = 350,
  className = "",
  isVisible = true,
  tutorialId,
}) => {
  const queryClient = useQueryClient();
  const [, forceUpdate] = React.useState({});
  const [instanceId] = React.useState(
    () => `${tutorialId}-${Math.random().toString(36).substr(2, 9)}`,
  );
  const { mutate: dismissTutorial } = useDismissTutorial({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
    },
  });

  const {
    data: userPreferences,
    isLoading: preferencesLoading,
    error: preferencesError,
  } = useGetUserPreferences();
  const dismissedTutorials = userPreferences?.dismissedTutorials ?? [];
  const isDismissed = dismissedTutorials.includes(tutorialId);
  const highestPriorityTutorial = getHighestPriorityTutorial();
  const firstInstanceOfTutorial = getFirstInstanceOfTutorial(tutorialId);
  const isCurrentlyActive =
    highestPriorityTutorial === tutorialId &&
    firstInstanceOfTutorial === instanceId;
  const shouldShow = isVisible && !isDismissed && isCurrentlyActive;

  // Assign priority when component mounts
  React.useEffect(() => {
    if (!tutorialPriorities[tutorialId]) {
      tutorialCounter++;
      tutorialPriorities[tutorialId] = tutorialCounter;
    }
  }, [tutorialId]);

  // Listen for global state changes
  React.useEffect(() => {
    const listener = () => forceUpdate({});
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  // Register this instance when component mounts
  React.useEffect(() => {
    if (!tutorialInstances[tutorialId]) {
      tutorialInstances[tutorialId] = [];
    }
    tutorialInstances[tutorialId].push(instanceId);

    return () => {
      // Clean up instance when component unmounts
      if (tutorialInstances[tutorialId]) {
        tutorialInstances[tutorialId] = tutorialInstances[tutorialId].filter(
          (id) => id !== instanceId,
        );
        if (tutorialInstances[tutorialId].length === 0) {
          delete tutorialInstances[tutorialId];
          visibleTutorials.delete(tutorialId);
        }
      }
    };
  }, [tutorialId, instanceId]);

  // Manage tutorial visibility
  React.useEffect(() => {
    const isEligible =
      isVisible && !isDismissed && !preferencesLoading && !preferencesError;

    if (isEligible) {
      // Only add if this is the first instance of this tutorialId to become eligible
      if (!visibleTutorials.has(tutorialId)) {
        visibleTutorials.add(tutorialId);
      }
    } else {
      // Only remove if this is the last instance of this tutorialId
      const instances = tutorialInstances[tutorialId] || [];
      const eligibleInstances = instances.filter((id) => id !== instanceId);
      if (eligibleInstances.length === 0) {
        visibleTutorials.delete(tutorialId);
      }
    }

    notifyListeners();
  }, [
    isVisible,
    isDismissed,
    preferencesLoading,
    preferencesError,
    tutorialId,
    instanceId,
  ]);

  if (!shouldShow || preferencesLoading || preferencesError || !tutorialId)
    return <>{children}</>;

  const handleClose = () => {
    visibleTutorials.delete(tutorialId);
    notifyListeners();
    dismissTutorial({ tutorialId });
    onClose?.();
  };

  const getContainerClasses = () => {
    const baseClasses = "relative block";
    return `${baseClasses} ${className}`;
  };

  const getTipClasses = () => {
    const baseClasses =
      "absolute bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50";

    switch (direction) {
      case "top":
        return `${baseClasses} bottom-full left-1/2 transform -translate-x-1/2`;
      case "bottom":
        return `${baseClasses} top-full left-1/2 transform -translate-x-1/2`;
      case "left":
        return `${baseClasses} right-full top-1/2 transform -translate-y-1/2`;
      case "right":
        return `${baseClasses} left-full top-1/2 transform -translate-y-1/2`;
      case "topleft":
        return `${baseClasses} bottom-full left-0`;
      case "topright":
        return `${baseClasses} bottom-full right-0`;
      case "bottomleft":
        return `${baseClasses} top-full left-0`;
      case "bottomright":
        return `${baseClasses} top-full right-0`;
      case "lefttop":
        return `${baseClasses} right-full top-0`;
      case "leftbottom":
        return `${baseClasses} right-full bottom-0`;
      case "righttop":
        return `${baseClasses} left-full top-0`;
      case "rightbottom":
        return `${baseClasses} left-full bottom-0`;
      default:
        return baseClasses;
    }
  };

  const getArrowClasses = () => {
    const baseArrowClasses = "absolute w-0 h-0";

    switch (direction) {
      case "top":
        return `${baseArrowClasses} top-full left-1/2 transform -translate-x-1/2 translate-y-[-1px] border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-white`;
      case "bottom":
        return `${baseArrowClasses} bottom-full left-1/2 transform -translate-x-1/2 translate-y-[1px] border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-white`;
      case "left":
        return `${baseArrowClasses} left-full top-1/2 transform -translate-y-1/2 translate-x-[-1px] border-t-[8px] border-b-[8px] border-l-[8px] border-t-transparent border-b-transparent border-l-white`;
      case "right":
        return `${baseArrowClasses} right-full top-1/2 transform -translate-y-1/2 translate-x-[1px] border-t-[8px] border-b-[8px] border-r-[8px] border-t-transparent border-b-transparent border-r-white`;
      case "topleft":
        return `${baseArrowClasses} top-full left-4 translate-y-[-1px] border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-white`;
      case "topright":
        return `${baseArrowClasses} top-full right-4 translate-y-[-1px] border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-white`;
      case "bottomleft":
        return `${baseArrowClasses} bottom-full left-4 translate-y-[1px] border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-white`;
      case "bottomright":
        return `${baseArrowClasses} bottom-full right-4 translate-y-[1px] border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent border-b-white`;
      case "lefttop":
        return `${baseArrowClasses} left-full top-4 translate-x-[-1px] border-t-[8px] border-b-[8px] border-l-[8px] border-t-transparent border-b-transparent border-l-white`;
      case "leftbottom":
        return `${baseArrowClasses} left-full bottom-4 translate-x-[-1px] border-t-[8px] border-b-[8px] border-l-[8px] border-t-transparent border-b-transparent border-l-white`;
      case "righttop":
        return `${baseArrowClasses} right-full top-4 translate-x-[1px] border-t-[8px] border-b-[8px] border-r-[8px] border-t-transparent border-b-transparent border-r-white`;
      case "rightbottom":
        return `${baseArrowClasses} right-full bottom-4 translate-x-[1px] border-t-[8px] border-b-[8px] border-r-[8px] border-t-transparent border-b-transparent border-r-white`;
      default:
        return baseArrowClasses;
    }
  };

  const getArrowBorderClasses = () => {
    const baseBorderClasses = "absolute w-0 h-0";

    switch (direction) {
      case "top":
        return `${baseBorderClasses} top-full left-1/2 transform -translate-x-1/2 border-l-[9px] border-r-[9px] border-t-[9px] border-l-transparent border-r-transparent border-t-gray-200`;
      case "bottom":
        return `${baseBorderClasses} bottom-full left-1/2 transform -translate-x-1/2 border-l-[9px] border-r-[9px] border-b-[9px] border-l-transparent border-r-transparent border-b-gray-200`;
      case "left":
        return `${baseBorderClasses} left-full top-1/2 transform -translate-y-1/2 border-t-[9px] border-b-[9px] border-l-[9px] border-t-transparent border-b-transparent border-l-gray-200`;
      case "right":
        return `${baseBorderClasses} right-full top-1/2 transform -translate-y-1/2 border-t-[9px] border-b-[9px] border-r-[9px] border-t-transparent border-b-transparent border-r-gray-200`;
      case "topleft":
        return `${baseBorderClasses} top-full left-4 border-l-[9px] border-r-[9px] border-t-[9px] border-l-transparent border-r-transparent border-t-gray-200`;
      case "topright":
        return `${baseBorderClasses} top-full right-4 border-l-[9px] border-r-[9px] border-t-[9px] border-l-transparent border-r-transparent border-t-gray-200`;
      case "bottomleft":
        return `${baseBorderClasses} bottom-full left-4 border-l-[9px] border-r-[9px] border-b-[9px] border-l-transparent border-r-transparent border-b-gray-200`;
      case "bottomright":
        return `${baseBorderClasses} bottom-full right-4 border-l-[9px] border-r-[9px] border-b-[9px] border-l-transparent border-r-transparent border-b-gray-200`;
      case "lefttop":
        return `${baseBorderClasses} left-full top-4 border-t-[9px] border-b-[9px] border-l-[9px] border-t-transparent border-b-transparent border-l-gray-200`;
      case "leftbottom":
        return `${baseBorderClasses} left-full bottom-4 border-t-[9px] border-b-[9px] border-l-[9px] border-t-transparent border-b-transparent border-l-gray-200`;
      case "righttop":
        return `${baseBorderClasses} right-full top-4 border-t-[9px] border-b-[9px] border-r-[9px] border-t-transparent border-b-transparent border-r-gray-200`;
      case "rightbottom":
        return `${baseBorderClasses} right-full bottom-4 border-t-[9px] border-b-[9px] border-r-[9px] border-t-transparent border-b-transparent border-r-gray-200`;
      default:
        return baseBorderClasses;
    }
  };

  return (
    <>
      {/* Dark overlay backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
      />

      <div
        className={`${getContainerClasses()} cursor-pointer relative z-50`}
        onClick={handleClose}
      >
        {children}
        <div
          className={getTipClasses()}
          style={{
            maxWidth: `${maxWidth}px`,
            width: "max-content",
          }}
        >
          {/* Arrow border (slightly larger for border effect) */}
          <div className={getArrowBorderClasses()}></div>
          {/* Arrow */}
          <div className={getArrowClasses()}></div>

          <div className="text-sm font-semibold text-gray-700 mb-2 pr-6 text-left">
            Quick Tip:
          </div>
          <div className="text-left">{content}</div>
          <div className="text-sm mt-2 text-electrum text-center">
            (Click to close)
          </div>
        </div>
      </div>
    </>
  );
};

export default TutorialTip;
